import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { CharacterLimitPlugin } from "@lexical/react/LexicalCharacterLimitPlugin";
import { CheckListPlugin } from "@lexical/react/LexicalCheckListPlugin";
import { ClearEditorPlugin } from "@lexical/react/LexicalClearEditorPlugin";
import { ClickableLinkPlugin } from "@lexical/react/LexicalClickableLinkPlugin";
// import {CollaborationPlugin} from '@lexical/react/LexicalCollaborationPlugin';
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HashtagPlugin } from "@lexical/react/LexicalHashtagPlugin";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { HorizontalRulePlugin } from "@lexical/react/LexicalHorizontalRulePlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { SelectionAlwaysOnDisplay } from "@lexical/react/LexicalSelectionAlwaysOnDisplay";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import { TablePlugin } from "@lexical/react/LexicalTablePlugin";
import { useLexicalEditable } from "@lexical/react/useLexicalEditable";
import { CAN_USE_DOM } from "@lexical/utils";
import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";

// import {createWebsocketProvider} from './collaboration';
import { useSettings } from "../LexicalEditor/context/SettingsContext";
import { useSharedHistoryContext } from "../LexicalEditor/context/SharedHistoryContext";
// import ActionsPlugin from '../LexicalEditor/plugins/ActionsPlugin';
// import AutocompletePlugin from '../LexicalEditor/plugins/AutocompletePlugin';
import AutoEmbedPlugin from "../LexicalEditor/plugins/AutoEmbedPlugin";
import AutoLinkPlugin from "../LexicalEditor/plugins/AutoLinkPlugin";
// import CodeActionMenuPlugin from '../LexicalEditor/plugins/CodeActionMenuPlugin';
import CodeHighlightPlugin from "../LexicalEditor/plugins/CodeHighlightPlugin";
import CollapsiblePlugin from "../LexicalEditor/plugins/CollapsiblePlugin";
// import CommentPlugin from '../LexicalEditor/plugins/CommentPlugin';
import ComponentPickerPlugin from "../LexicalEditor/plugins/ComponentPickerPlugin";
import ContextMenuPlugin from "../LexicalEditor/plugins/ContextMenuPlugin";
import DragDropPaste from "../LexicalEditor/plugins/DragDropPastePlugin";
import HistoryPrimeOnPastePlugin from "../LexicalEditor/plugins/HistoryPrimeOnPastePlugin";
import PasteImagePlugin from "../LexicalEditor/plugins/PasteImagePlugin";
import TablePastePlugin from "../LexicalEditor/plugins/TablePastePlugin";
import DraggableBlockPlugin from "../LexicalEditor/plugins/DraggableBlockPlugin";
import EmojiPickerPlugin from "../LexicalEditor/plugins/EmojiPickerPlugin";
import EmojisPlugin from "../LexicalEditor/plugins/EmojisPlugin";
import EquationsPlugin from "../LexicalEditor/plugins/EquationsPlugin";
// import ExcalidrawPlugin from '../LexicalEditor/plugins/ExcalidrawPlugin';
import FigmaPlugin from "../LexicalEditor/plugins/FigmaPlugin";
import FloatingLinkEditorPlugin from "../LexicalEditor/plugins/FloatingLinkEditorPlugin";
import FloatingTextFormatToolbarPlugin from "../LexicalEditor/plugins/FloatingTextFormatToolbarPlugin";
import ImagesPlugin from "../LexicalEditor/plugins/ImagesPlugin";
import InlineImagePlugin from "../LexicalEditor/plugins/InlineImagePlugin";
import KeywordsPlugin from "../LexicalEditor/plugins/KeywordsPlugin";
import { LayoutPlugin } from "../LexicalEditor/plugins/LayoutPlugin/LayoutPlugin";
import LinkPlugin from "../LexicalEditor/plugins/LinkPlugin";
// import MarkdownShortcutPlugin from '../LexicalEditor/plugins/MarkdownShortcutPlugin';
import { MaxLengthPlugin } from "../LexicalEditor/plugins/MaxLengthPlugin";
import MentionsPlugin from "../LexicalEditor/plugins/MentionsPlugin";
import PageBreakPlugin from "../LexicalEditor/plugins/PageBreakPlugin";
// import ShortcutsPlugin from '../LexicalEditor/plugins/ShortcutsPlugin';
import SpecialTextPlugin from "../LexicalEditor/plugins/SpecialTextPlugin";
import SpeechToTextPlugin from "../LexicalEditor/plugins/SpeechToTextPlugin";
import TabFocusPlugin from "../LexicalEditor/plugins/TabFocusPlugin";
import TableCellActionMenuPlugin from "../LexicalEditor/plugins/TableActionMenuPlugin";
import TableCellResizer from "../LexicalEditor/plugins/TableCellResizer";
import TableHoverActionsPlugin from "../LexicalEditor/plugins/TableHoverActionsPlugin";
import TableOfContentsPlugin from "../LexicalEditor/plugins/TableOfContentsPlugin";
import ToolbarPlugin from "../LexicalEditor/plugins/ToolbarPlugin";
import TwitterPlugin from "../LexicalEditor/plugins/TwitterPlugin";
import YouTubePlugin from "../LexicalEditor/plugins/YouTubePlugin";
import ContentEditables from "../LexicalEditor/ui/ContentEditable";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import "./index.css";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import {
  $getRoot,
  CAN_UNDO_COMMAND,
  CAN_REDO_COMMAND,
  COMMAND_PRIORITY_LOW,
} from "lexical";
import PollPlugin from "../LexicalEditor/plugins/PollPlugin";

export default function Editor({
  handleEditorChange,
  initialContent,
  placeholder,
  onDirtyChange,
  showToolbarWhenReadOnly = false,
}) {
  const { historyState } = useSharedHistoryContext();
  const {
    settings: {
      isCollab,
      isAutocomplete,
      isMaxLength,
      isCharLimit,
      hasLinkAttributes,
      isCharLimitUtf8,
      isRichText,
      showTreeView,
      showTableOfContents,
      shouldUseLexicalContextMenu,
      shouldPreserveNewLinesInMarkdown,
      tableCellMerge,
      tableCellBackgroundColor,
      tableHorizontalScroll,
      shouldAllowHighlightingWithBrackets,
      selectionAlwaysOnDisplay,
      listStrictIndent,
    },
  } = useSettings();
  const isEditable = useLexicalEditable();
  const placeholderText = placeholder || (isCollab
    ? "Enter some collaborative rich text..."
    : isRichText
      ? "Enter some rich text..."
      : "Enter some plain text...");
  const [floatingAnchorElem, setFloatingAnchorElem] = useState(null);
  const [isSmallWidthViewport, setIsSmallWidthViewport] = useState(false);
  const [editor] = useLexicalComposerContext();

  const [activeEditor, setActiveEditor] = useState(editor);
  const [isLinkEditMode, setIsLinkEditMode] = useState(false);
  const hasSetInitialContent = useRef(false);

  // Bubble Lexical's undo/redo-available flags up to the parent as a "dirty" signal.
  // The editor is considered dirty when EITHER the undo OR the redo icon is active:
  //   - canUndo=true  → user has made edits that haven't been fully undone
  //   - canRedo=true  → user had made edits and partially/fully undone them
  // Both states indicate the history is non-pristine since the last save/init.
  // We use refs to track each flag independently so either command can update
  // the combined dirty state without the other's last value being lost.
  const canUndoRef = useRef(false);
  const canRedoRef = useRef(false);

  useEffect(() => {
    const unregisterUndo = editor.registerCommand(
      CAN_UNDO_COMMAND,
      (canUndo) => {
        canUndoRef.current = canUndo;
        if (onDirtyChange) onDirtyChange(canUndoRef.current || canRedoRef.current);
        return false;
      },
      COMMAND_PRIORITY_LOW,
    );
    const unregisterRedo = editor.registerCommand(
      CAN_REDO_COMMAND,
      (canRedo) => {
        canRedoRef.current = canRedo;
        if (onDirtyChange) onDirtyChange(canUndoRef.current || canRedoRef.current);
        return false;
      },
      COMMAND_PRIORITY_LOW,
    );
    return () => {
      unregisterUndo();
      unregisterRedo();
    };
  }, [editor, onDirtyChange]);

  // Set initial content when editor is ready and initialContent is provided
  useEffect(() => {
    if (
      initialContent &&
      initialContent.trim() !== "" &&
      !hasSetInitialContent.current
    ) {
      hasSetInitialContent.current = true;

      // Set content once with a delay to ensure editor is ready
      setTimeout(() => {
        editor.update(() => {
          try {
            // Check if editor already has content
            const root = $getRoot();
            const children = root.getChildren();

            // Only set content if editor is empty
            if (children.length === 0) {
              // Parse HTML and set it as the editor content
              const parser = new DOMParser();
              const dom = parser.parseFromString(initialContent, "text/html");

              // Generate nodes from DOM using the proper import function
              const nodes = $generateNodesFromDOM(editor, dom, {
                preserveWhitespace: true,
              });

              if (nodes && nodes.length > 0) {
                root.clear();
                root.append(...nodes);
              }
            }
          } catch (error) {
            console.error("Editor: Error setting initial content:", error);
          }
        });

        // After the programmatic init update is committed, wipe the history so
        // the undo button stays inactive and onDirtyChange stays false until
        // the user actually edits.  setTimeout(0) runs after Lexical's internal
        // microtask queue has flushed the update and notified HistoryPlugin.
        // CRITICAL: Set current (not null) so the first user edit (e.g. paste)
        // will push this baseline to undoStack. Lexical only pushes when
        // current !== null, so priming allows undo from the very first edit.
        setTimeout(() => {
          try {
            if (historyState) {
              // Guard: if the user already made an edit (e.g. pasted an image)
              // before this delayed init cleanup fires, don't wipe their history.
              if (historyState.undoStack.length > 0) {
                return;
              }
              historyState.undoStack = [];
              historyState.redoStack = [];
              historyState.current = {
                editor,
                editorState: editor.getEditorState(),
              };
            }
            // Re-broadcast the new (empty) undo/redo state so the toolbar and
            // our CAN_UNDO_COMMAND listener both reflect the cleared history.
            editor.dispatchCommand(CAN_UNDO_COMMAND, false);
            editor.dispatchCommand(CAN_REDO_COMMAND, false);
          } catch (_e) { /* ignore */ }
        }, 0);
      }, 100);
    }
  }, [editor, initialContent, historyState]); // Add initialContent back but with ref protection

  // Prime history so first paste/edit gets proper undo. Lexical only pushes to
  // undo when current !== null. We prime: (1) on next tick for empty editor,
  // (2) on first focus so we're ready before user can paste.
  useEffect(() => {
    if (!historyState || !editor) return;

    const primeHistory = () => {
      if (historyState.current === null && historyState.undoStack.length === 0) {
        try {
          historyState.current = {
            editor,
            editorState: editor.getEditorState(),
          };
        } catch (_e) { /* ignore */ }
      }
    };

    const timer = setTimeout(primeHistory, 0);

    const root = editor.getRootElement();
    const onFocus = () => {
      primeHistory();
    };
    root?.addEventListener('focus', onFocus, { capture: true, once: true });

    return () => {
      clearTimeout(timer);
      root?.removeEventListener('focus', onFocus, { capture: true });
    };
  }, [editor, historyState]);

  const onRef = (_floatingAnchorElem) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };

  useEffect(() => {
    const updateViewPortWidth = () => {
      const isNextSmallWidthViewport =
        CAN_USE_DOM && window.matchMedia("(max-width: 1025px)").matches;

      if (isNextSmallWidthViewport !== isSmallWidthViewport) {
        setIsSmallWidthViewport(isNextSmallWidthViewport);
      }
    };
    updateViewPortWidth();
    window.addEventListener("resize", updateViewPortWidth);

    return () => {
      window.removeEventListener("resize", updateViewPortWidth);
    };
  }, [isSmallWidthViewport]);

  // Debounce serialization to reduce lag: run on pause (e.g. 400ms) and flush on blur
  const onChangeTimerRef = useRef(null);

  const flushEditorContent = useCallback(() => {
    if (onChangeTimerRef.current) {
      clearTimeout(onChangeTimerRef.current);
      onChangeTimerRef.current = null;
    }
    if (!handleEditorChange) return;
    try {
      editor.getEditorState().read(() => {
        let content = $generateHtmlFromNodes(editor);
        content = `<div class="editor-shell">${content}</div>`;
        handleEditorChange(content);
      });
    } catch (e) {
    }
  }, [editor, handleEditorChange]);

  const handleEditorOnChange = useCallback(
    (_editorState) => {
      if (!handleEditorChange) return;
      if (onChangeTimerRef.current) clearTimeout(onChangeTimerRef.current);
      onChangeTimerRef.current = setTimeout(flushEditorContent, 400);
    },
    [handleEditorChange, flushEditorContent]
  );

  useEffect(() => {
    const root = editor.getRootElement();
    if (!root) return;
    const onBlur = () => flushEditorContent();
    root.addEventListener("blur", onBlur, true);
    return () => {
      root.removeEventListener("blur", onBlur, true);
      // Flush pending content on unmount so we don't lose last edits
      flushEditorContent();
    };
  }, [editor, flushEditorContent]);

  return (
    <>
      {isRichText && (isEditable || showToolbarWhenReadOnly) && (
        <ToolbarPlugin
          editor={editor}
          activeEditor={activeEditor}
          setActiveEditor={setActiveEditor}
          setIsLinkEditMode={setIsLinkEditMode}
        />
      )}
      <OnChangePlugin onChange={handleEditorOnChange} />

      {/* {isRichText && (
        <ShortcutsPlugin
          editor={activeEditor}
          setIsLinkEditMode={setIsLinkEditMode}
        />
      )} */}
      <div
        className={`editor-containerss ${showTreeView ? "tree-view" : ""} ${!isRichText ? "plain-text" : ""
          }`}
      >
        {isMaxLength && <MaxLengthPlugin maxLength={30} />}
        <HistoryPrimeOnPastePlugin />
        <DragDropPaste />
        <PasteImagePlugin />
        <TablePastePlugin />
        <AutoFocusPlugin />
        {selectionAlwaysOnDisplay && <SelectionAlwaysOnDisplay />}
        <ClearEditorPlugin />
        <ComponentPickerPlugin />
        <EmojiPickerPlugin />
        <AutoEmbedPlugin />
        <MentionsPlugin />
        <EmojisPlugin />
        <HashtagPlugin />
        <KeywordsPlugin />
        <SpeechToTextPlugin />
        <AutoLinkPlugin />
        {/* <CommentPlugin
          providerFactory={isCollab ? createWebsocketProvider : undefined}
        /> */}
        {/* {isRichText ? ( */}
        <>
          <RichTextPlugin
            contentEditable={
              <div className="editor-scroller">
                <div className="editor" ref={onRef}>
                  <ContentEditables placeholder={placeholderText} />
                </div>
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          {/* <MarkdownShortcutPlugin /> */}
          <CodeHighlightPlugin />
          <ListPlugin hasStrictIndent={listStrictIndent} />
          <CheckListPlugin />
          <TablePlugin
            hasCellMerge={tableCellMerge}
            hasCellBackgroundColor={tableCellBackgroundColor}
            hasHorizontalScroll={tableHorizontalScroll}
          />
          <TableCellResizer />
          <ImagesPlugin />
          <InlineImagePlugin />
          <LinkPlugin hasLinkAttributes={hasLinkAttributes} />
          <PollPlugin />
          <TwitterPlugin />
          <YouTubePlugin />
          <FigmaPlugin />
          <ClickableLinkPlugin disabled={isEditable} />
          <HorizontalRulePlugin />
          <EquationsPlugin />
          <HistoryPlugin externalHistoryState={historyState} delay={100} />

          {/* <ExcalidrawPlugin /> */}
          <TabFocusPlugin />
          <TabIndentationPlugin maxIndent={7} />
          <CollapsiblePlugin />
          <PageBreakPlugin />
          <LayoutPlugin />
          {floatingAnchorElem && isEditable && (
            <>
              <FloatingLinkEditorPlugin
                anchorElem={floatingAnchorElem}
                isLinkEditMode={isLinkEditMode}
                setIsLinkEditMode={setIsLinkEditMode}
              />
              <TableCellActionMenuPlugin
                anchorElem={floatingAnchorElem}
                cellMerge={true}
              />
            </>
          )}
          {floatingAnchorElem && isEditable && !isSmallWidthViewport && (
            <>
              <DraggableBlockPlugin anchorElem={floatingAnchorElem} />
              {/* <CodeActionMenuPlugin anchorElem={floatingAnchorElem} /> */}
              <TableHoverActionsPlugin anchorElem={floatingAnchorElem} />
              <FloatingTextFormatToolbarPlugin
                anchorElem={floatingAnchorElem}
                setIsLinkEditMode={setIsLinkEditMode}
              />
            </>
          )}
        </>
        {/* ) : ( */}
        <>
          {/* <PlainTextPlugin
              contentEditable={<ContentEditables placeholder={placeholder} />}
              ErrorBoundary={LexicalErrorBoundary}
            /> */}
          {/* <HistoryPlugin externalHistoryState={historyState} /> */}
        </>
        {/* )} */}
        {(isCharLimit || isCharLimitUtf8) && (
          <CharacterLimitPlugin
            charset={isCharLimit ? "UTF-16" : "UTF-8"}
            maxLength={5}
          />
        )}
        {/* {isAutocomplete && <AutocompletePlugin />} */}
        <div>{showTableOfContents && <TableOfContentsPlugin />}</div>
        {shouldUseLexicalContextMenu && <ContextMenuPlugin />}
        {shouldAllowHighlightingWithBrackets && <SpecialTextPlugin />}
        {/* <ActionsPlugin
          isRichText={isRichText}
          shouldPreserveNewLinesInMarkdown={shouldPreserveNewLinesInMarkdown}
        /> */}
      </div>
    </>
  );
}
